import { useParams, Navigate, Link } from "react-router-dom";
import { Check, ArrowRight } from "lucide-react";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";
import SectionHeader from "@/components/SectionHeader";
import ProcessSection from "@/sections/ProcessSection";
import CTASection from "@/components/CTASection";
import { LinkButton } from "@/components/Button";
import { SERVICES } from "@/lib/content";

const ServiceDetail = () => {
  const { slug } = useParams();
  const service = SERVICES.find((s) => s.slug === slug);
  if (!service) return <Navigate to="/404" replace />;
  const Icon = service.icon;

  return (
    <>
      <Seo
        title={`${service.name} | Kryvo Technologies`}
        description={service.shortDesc}
      />

      {/* Hero */}
      <section className="grid-bg bg-kryvo-mist pb-16 pt-[calc(72px+4rem)] sm:pb-20" data-testid="service-detail-hero">
        <div className="container-x max-w-3xl">
          <Reveal>
            <Link to="/services" className="text-sm font-medium text-kryvo-green hover:text-kryvo-deep" data-testid="service-back-link">
              ← All Services
            </Link>
            <div className="mt-6 inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-kryvo-soft text-kryvo-green">
              <Icon className="h-7 w-7" aria-hidden="true" />
            </div>
            <h1 className="mt-6 text-balance text-4xl font-semibold text-kryvo-ink sm:text-5xl">
              {service.name}
            </h1>
            <p className="mt-3 font-display text-xl font-medium text-kryvo-green">{service.tagline}</p>
            <p className="mt-5 text-base leading-relaxed text-kryvo-ink/65 sm:text-lg">{service.overview}</p>
            <div className="mt-8">
              <LinkButton to="/contact" variant="primary" testId="service-hero-cta">
                Start a Project
              </LinkButton>
            </div>
          </Reveal>
        </div>
      </section>

      {/* What we build */}
      <section className="container-x py-16 sm:py-20" data-testid="service-what-we-build">
        <div className="grid gap-10 lg:grid-cols-2 lg:items-center">
          <SectionHeader
            align="left"
            eyebrow="What We Build"
            title={`${service.name} — delivered end to end`}
            description="Every engagement is scoped around your goals. Typical outcomes include:"
          />
          <Reveal>
            <ul className="flex flex-col gap-3">
              {service.whatWeBuild.map((item) => (
                <li key={item} className="flex items-center gap-3 rounded-xl border border-kryvo-green/10 bg-kryvo-mist/60 px-5 py-4 text-sm font-medium text-kryvo-ink">
                  <Check className="h-5 w-5 shrink-0 text-kryvo-green" aria-hidden="true" />
                  {item}
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>

      {/* Capabilities */}
      <section className="bg-kryvo-mist py-16 sm:py-20" data-testid="service-capabilities">
        <div className="container-x">
          <SectionHeader
            eyebrow="Key Capabilities"
            title="What you get with Kryvo"
          />
          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {service.capabilities.map((c, i) => (
              <Reveal key={c.title} delay={i * 0.06}>
                <div className="h-full rounded-2xl border border-kryvo-green/10 bg-white p-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_20px_45px_-22px_rgba(6,78,42,0.3)]">
                  <h3 className="text-lg font-semibold text-kryvo-ink">{c.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-kryvo-ink/60">{c.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <ProcessSection />

      {/* Use cases */}
      <section className="container-x pb-16 sm:pb-20" data-testid="service-use-cases">
        <SectionHeader eyebrow="Use Cases" title="Where this makes a difference" />
        <div className="mt-14 grid gap-6 sm:grid-cols-2">
          {service.useCases.map((u, i) => (
            <Reveal key={u.title} delay={i * 0.06}>
              <div className="group h-full rounded-2xl border border-kryvo-green/10 bg-white p-7 transition-colors duration-300 hover:border-kryvo-green/30">
                <span className="font-display text-sm font-semibold text-kryvo-lime">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <h3 className="mt-2 text-xl font-semibold text-kryvo-ink">{u.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-kryvo-ink/60">{u.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Why Kryvo strip */}
      <section className="container-x pb-16" data-testid="service-why-kryvo">
        <Reveal>
          <div className="flex flex-col items-start justify-between gap-6 rounded-2xl border border-kryvo-line bg-kryvo-mist p-8 sm:flex-row sm:items-center sm:p-10">
            <div>
              <h2 className="text-2xl font-semibold text-kryvo-ink">Why businesses choose Kryvo</h2>
              <p className="mt-2 max-w-xl text-sm leading-relaxed text-kryvo-sub">
                One partner for strategy, design and engineering — so your product moves from idea to
                launch without handoffs, delays or diluted quality.
              </p>
            </div>
            <Link to="/about" data-testid="service-why-link" className="group inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-kryvo-green px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-kryvo-deep">
              About Kryvo
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" aria-hidden="true" />
            </Link>
          </div>
        </Reveal>
      </section>

      <CTASection />
    </>
  );
};

export default ServiceDetail;
